import type React from 'react';
import { useState } from 'react';
import styled from 'styled-components';

import type { InteractionTrackingEvent } from '@xing-com/crate-communication-tracking';
import { useTracking } from '@xing-com/crate-communication-tracking';
import { SEARCH_ALERTS_MODAL_TYPES } from '@xing-com/crate-jobs-constants';
import {
  useSearchAlert,
  useSearchAlertParams,
  useUserEmail,
} from '@xing-com/crate-jobs-hooks';
import { useJobsNwtTracking } from '@xing-com/crate-jobs-tracking';
import { useLoginState } from '@xing-com/hub';
import { IllustrationSpotBell } from '@xing-com/illustrations';
import { PopUpWindow } from '@xing-com/pop-up-window';
import { space3XL, spaceXL } from '@xing-com/tokens';

import { CreateAlert } from './create-alert';
import { CreatedAlert } from './created-alert';

const StyledIllustrationSpotBell = styled(IllustrationSpotBell)`
  width: 100%;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${spaceXL};
  padding: ${space3XL} ${spaceXL};
`;

type Props = {
  isThirdParty: boolean;
};

export const JobDetailsCreateAlertModal: React.FC<Props> = ({
  isThirdParty,
}) => {
  const { isModalActive, onClose } = useSearchAlert();
  const summary = useSearchAlertParams();
  const { email } = useUserEmail();
  const [didCreateAlert, setDidCreateAlert] = useState(false);
  const { track } =
    useTracking<InteractionTrackingEvent<{ PropTrackAction: string }>>();
  const trackNwtEvent = useJobsNwtTracking();
  const loginState = useLoginState();

  const { JOB_DETAILS } = SEARCH_ALERTS_MODAL_TYPES;
  const isActive =
    isModalActive([JOB_DETAILS]) && (!!summary || didCreateAlert);

  const onCreated = (alertUrn: string): void => {
    track({
      type: 'interaction',
      event: 'PropTrackAction',
      PropTrackAction: 'jobs_search_alert_create_confirm',
    });

    const nwtEvent =
      loginState === 'LOGGED_OUT'
        ? {
            event: 'intended',
            intention: 'activate_search_alert',
            itemUrn: `urn:x-xing:job_search_alerts:alert:logged_out`,
          }
        : {
            event: 'activated',
            itemUrn: alertUrn,
          };

    trackNwtEvent({
      eventSchema: 'basic',
      page: isThirdParty ? 'jobs/detail_external' : 'jobs/detail',
      element: 'search_alert',
      elementDetail: 'job_detail',
      ...nwtEvent,
    });

    setDidCreateAlert(true);
  };

  const close = (action: 'okay' | 'cancel'): void => {
    track({
      type: 'interaction',
      event: 'PropTrackAction',
      PropTrackAction: `jobs_search_alert_create_${action}`,
    });
    onClose();
  };

  return (
    <PopUpWindow
      isShown={isActive}
      hide={didCreateAlert ? () => close('cancel') : onClose}
      onDimmerClick={didCreateAlert ? () => close('cancel') : onClose}
      noPadding
    >
      <Container>
        <StyledIllustrationSpotBell size="large" />
        {didCreateAlert ? (
          <CreatedAlert onClose={() => close('okay')} email={email} />
        ) : (
          <CreateAlert
            query={summary}
            isThirdParty={isThirdParty}
            onCompleted={onCreated}
            onCancel={onClose}
          />
        )}
      </Container>
    </PopUpWindow>
  );
};
